import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ExportIcon from "@mui/icons-material/OpenInNew";
import WarningIcon from "@mui/icons-material/Warning";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { usePayerProfileTopHCPExport } from "../../api/usePayerProfileApi";
import TableHeader from "../../components/Headers/TableHeader";
import SimpleTable from "../../components/Table/SimpleTable";
import { Column, Sort } from "../../components/Table/types";
import Tooltip from "../../components/Tooltip/Tooltip";
import { useIndicationBasedTopHCPConfiguration } from "../../data/global/useProductConfiguration";
import { TopHealthcareProviders as ApiResponse, SortDirection } from "../../generate";
import useGetRole from "../../hooks/useGetRole";
import { useClientRoutesAndLabels } from "../../ui/routes";

interface Props {
  channel: string;
  hcpData: ApiResponse[] | undefined | null;
  loading: boolean;
  stateInformation: {
    client: string;
    id: string;
    timePeriod?: string;
    indication?: string;
  };
  indication?: string | null;
  sort?: Sort;
  onSortChange?: (sort?: Sort) => void;
}

const TopHealthcareProviders: React.FC<Props> = ({
  channel,
  hcpData,
  loading,
  stateInformation,
  indication,
  sort,
  onSortChange,
}) => {
  const { t, i18n } = useTranslation();

  const selectedClient = stateInformation.client;

  const routes = useClientRoutesAndLabels(selectedClient);

  const { id, hcpID } = useParams<{ id: string; hcpID: string | undefined }>();

  const { data: role } = useGetRole(selectedClient);

  const {
    dataKeys: allowedDataKeys,
    hideViewAllButton = false,
    useIndicationInNames = false,
  } = useIndicationBasedTopHCPConfiguration(selectedClient, indication, String(role));

  // Helper to map between UI field names and server field names
  const mapServerToUIField = (field: string) => (field === "full_name" ? "topHCPs" : field);
  const mapUIToServerField = (field: string) => (field === "topHCPs" ? "full_name" : field);

  const client_indication_key = useMemo(() => {
    return useIndicationInNames ? selectedClient + "_" + indication?.replaceAll(" ", "") : selectedClient;
  }, [selectedClient, indication, useIndicationInNames]);

  const HealthcareProviderName = "healthcareProviderName" as const;
  const availableColumns: Column[] = useMemo(
    () => [
      {
        label: t([`planProfile.topHcps.${selectedClient}`, `planProfile.topHcps.default`]),
        align: "left",
        dataKey: "topHCPs",
        tdClasses: "bg-light-blue",
        component: HealthcareProviderName,
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.fav_pct`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.fav_pct`)
          : t("planProfile.topHcpColumnHeaders.fav_pct"),
        align: "right",
        dataKey: "fav_pct",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.acc_pct`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.acc_pct`)
          : t("planProfile.topHcpColumnHeaders.acc_pct"),
        align: "right",
        dataKey: "acc_pct",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.bobPctOvrl`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.bobPctOvrl`)
          : t("planProfile.topHcpColumnHeaders.bobPctOvrl"),
        align: "right",
        dataKey: "bob_pct_ovrl",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.marketTrx`)
          ? t([
              `planProfile.topHcpColumnHeaders.${client_indication_key}.marketTrx.${stateInformation.timePeriod}`,
              `planProfile.topHcpColumnHeaders.${client_indication_key}.marketTrx`,
            ])
          : t(`planProfile.topHcpColumnHeaders.marketTrx`),
        align: "right",
        dataKey: "mkt_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_share`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_share`, {
              product: selectedClient?.split("_")[0],
            })
          : t(`planProfile.topHcpColumnHeaders.prod_share`, { product: selectedClient?.split("_")[0] }),
        align: "right",
        dataKey: "prod_share",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.comp_prod_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.comp_prod_trx`, {
              product: selectedClient,
              channel,
            })
          : t("planProfile.topHcpColumnHeaders.comp_prod_trx", { product: selectedClient, channel }),
        align: "right",
        dataKey: "comp_prod_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_comp_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_comp_trx`, {
              product: selectedClient,
              channel,
            })
          : t("planProfile.topHcpColumnHeaders.chnl_comp_trx", {
              product: selectedClient,
              channel,
            }),
        align: "right",
        dataKey: "chnl_comp_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_mkt_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_mkt_trx`, { channel })
          : t("planProfile.topHcpColumnHeaders.chnl_mkt_trx", { channel }),
        align: "right",
        dataKey: "chnl_mkt_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_prod_share`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_prod_share`, {
              product: selectedClient,
              channel,
            })
          : t("planProfile.topHcpColumnHeaders.chnl_prod_share", { product: selectedClient, channel }),
        align: "right",
        dataKey: "chnl_prod_share",
        format: {
          suffix: "%",
          decimalScale: 1,
          fixedDecimalScale: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_trx`, { product: selectedClient })
          : t("planProfile.topHcpColumnHeaders.prod_trx", { product: selectedClient }),
        align: "right",
        dataKey: "prod_trx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_nrx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.prod_nrx`, { product: selectedClient })
          : t("planProfile.topHcpColumnHeaders.prod_nrx", { product: selectedClient }),
        align: "right",
        dataKey: "prod_nrx",
        format: {
          thousandSeparator: true,
        },
      },
      {
        label: i18n.exists(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_prod_trx`)
          ? t(`planProfile.topHcpColumnHeaders.${client_indication_key}.chnl_prod_trx`, {
              product: selectedClient,
              channel,
            })
          : t("planProfile.topHcpColumnHeaders.chnl_prod_trx", { product: selectedClient, channel }),
        align: "right",
        dataKey: "chnl_prod_trx",
        format: {
          thousandSeparator: true,
        },
      },
    ],
    [channel, selectedClient, client_indication_key, t, i18n, stateInformation.timePeriod],
  );

  // Initialize sort state if not provided
  // Map server field names to UI field names for both initialization and incoming props
  const currentSort = useMemo(() => {
    if (sort) {
      return {
        ...sort,
        field: mapServerToUIField(sort.field),
      };
    }
    // No sort active - backend will use default multi-column sort
    return undefined;
  }, [sort]);

  const handleSort = useCallback(
    (newSort: Sort) => {
      const column = availableColumns.find((col) => col.dataKey === newSort.field);
      const startDirection = column?.format ? SortDirection.Descending : SortDirection.Ascending;

      // Compare using UI field names (newSort.field is already UI, sort.field needs mapping)
      const currentSortField = sort ? mapServerToUIField(sort.field) : undefined;

      if (currentSortField === newSort.field && newSort.direction === startDirection) {
        onSortChange?.(undefined);
        return;
      }

      // Map UI field names to server field names
      const sortToSend = {
        ...newSort,
        field: mapUIToServerField(newSort.field),
      };
      onSortChange?.(sortToSend);
    },
    [onSortChange, sort, availableColumns],
  );

  const columns: Column[] = useMemo(() => {
    if (!allowedDataKeys || allowedDataKeys.length === 0) {
      return [];
    }

    const columnMap = new Map<string, Column>();
    availableColumns.forEach((column: Column) => {
      columnMap.set(column.dataKey, column);
    });

    return allowedDataKeys
      .map((dataKey: string) => columnMap.get(dataKey))
      .filter((column: Column | undefined): column is Column => {
        return column !== undefined;
      });
  }, [availableColumns, allowedDataKeys]);

  const columnKeys = columns.map((column) => column.dataKey);
  const columnNames = columns.map((column) => column.label);

  const exportTable = usePayerProfileTopHCPExport(
    stateInformation.id,
    columnKeys,
    columnNames,
    stateInformation.client,
    20,
    stateInformation.timePeriod,
    stateInformation.indication,
  );

  // Server is handling the sorting, so just return the data as-is
  const sortedData = hcpData;

  return (
    <div className="pb-6">
      <TableHeader
        title={
          i18n.exists(`planProfile.topProvider.${selectedClient}`)
            ? t(`planProfile.topProvider.${selectedClient}`)
            : t(`planProfile.topProvider.default`)
        }
      >
        <Tooltip
          placement="bottom-end"
          tip={t([`planProfile.topProviderTooltip.${selectedClient}`, `planProfile.topProviderTooltip.default`])}
        >
          <InfoOutlined className="text-primary" />
        </Tooltip>
      </TableHeader>
      <SimpleTable
        columns={columns}
        data={sortedData}
        loading={loading}
        sort={currentSort}
        handleSort={handleSort}
        tableFooter={
          <tfoot>
            <tr>
              <td colSpan={10} className="sm:text-right p-0">
                <div className="bg-rules flex items-center justify-between">
                  {!hideViewAllButton && (
                    <Link
                      to={
                        hcpID
                          ? routes.client.dpp.payers.show.pinned.providers.with({ client: selectedClient, id, hcpID })
                          : routes.client.dpp.payers.show.providers.with({ client: selectedClient, id: id })
                      }
                      className="btn btn-primary w-9/12 m-1.5"
                    >
                      <span>{t("planProfile.viewAllHcps")}</span>
                    </Link>
                  )}
                  <div className="flex">
                    <button className="btn p-0" disabled={exportTable.isLoading} onClick={() => exportTable.mutate()}>
                      {(exportTable.status === "idle" || exportTable.status === "success") && (
                        <span>{t("common.export")}</span>
                      )}
                      {exportTable.status === "loading" && <span>{t("common.exportLoading")}</span>}
                      {exportTable.status === "error" && (
                        <>
                          <WarningIcon /> <span>{t("common.export")}</span>
                        </>
                      )}
                    </button>

                    <button
                      className="btn p-0"
                      aria-label={t("common.export")}
                      disabled={exportTable.isLoading}
                      onClick={() => exportTable.mutate()}
                    >
                      <ExportIcon />
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        }
      />
    </div>
  );
};

export default TopHealthcareProviders;
